import { useEffect, useState, useCallback } from "react";
import { LogOut, RefreshCw, Trash2, Filter, Mail, Phone, Building2, Calendar, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed"];
const STATUS_COLORS = {
  new: "border-[#0891B2] text-[#0891B2] bg-[#0891B2]/5",
  contacted: "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/5",
  qualified: "border-emerald-600 text-emerald-700 bg-emerald-50",
  closed: "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-500 bg-slate-50 dark:bg-slate-900",
};

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads" + (filter !== "all" ? `?status=${filter}` : "")),
        api.get("/leads/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* Top bar */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/leadtech-icon.png" alt="LeadTech Connect" className="h-6 w-auto" />
            <span className="font-heading font-bold text-slate-900 dark:text-white text-sm">LeadTech.Connect · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 dark:text-slate-500 hidden md:inline">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 hover:text-slate-900 border border-slate-300 dark:border-slate-700 hover:border-slate-900 px-3 py-1.5 transition-colors"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-8 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 data-testid="admin-heading" className="font-heading font-black text-3xl md:text-4xl tracking-tight text-slate-900 dark:text-white">Leads</h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">Manage inbound inquiries and pipeline.</p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 hover:text-slate-900 border border-slate-300 dark:border-slate-700 hover:border-slate-900 px-3 py-2 transition-colors"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {stats && [
            { label: "Total", value: stats.total, testid: "stat-total" },
            { label: "New", value: stats.new, testid: "stat-new" },
            { label: "Contacted", value: stats.contacted, testid: "stat-contacted" },
            { label: "Qualified", value: stats.qualified, testid: "stat-qualified" },
            { label: "Closed", value: stats.closed, testid: "stat-closed" },
            { label: "This Week", value: stats.this_week, testid: "stat-week" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-5 rounded-sm shadow-sm">
              <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">{s.label}</div>
              <div className="font-heading font-black text-3xl text-slate-900 dark:text-white mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Filter size={14} className="text-slate-500 dark:text-slate-500" aria-hidden="true" />
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                filter === s
                  ? "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/5"
                  : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-900"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Company</th>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Service</th>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Received</th>
                  <th className="text-right px-4 py-3 font-heading text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500 dark:text-slate-500">Loading…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500 dark:text-slate-500" data-testid="leads-empty">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)}>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 dark:text-white font-medium flex items-center gap-2">
                        {l.name}
                        {l.attachment && <Paperclip size={12} className="text-[#0891B2]" aria-label="Has attachment" />}
                      </div>
                      <div className="text-slate-500 dark:text-slate-500 text-xs">{l.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{l.company}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{l.service}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs uppercase tracking-widest border px-2 py-1 ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`delete-lead-${l.id}`}
                        onClick={(e) => { e.stopPropagation(); remove(l.id); }}
                        className="text-slate-400 dark:text-slate-500 hover:text-red-500 p-1"
                        aria-label="Delete lead"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={() => setSelected(null)} aria-hidden="true" />
          <div className="w-full max-w-md bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 overflow-y-auto shadow-2xl" data-testid="lead-detail">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white">Lead Details</h2>
              <button data-testid="close-detail" onClick={() => setSelected(null)} className="text-slate-500 dark:text-slate-500 hover:text-slate-900 text-sm">Close</button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <div className="font-heading font-black text-2xl text-slate-900 dark:text-white">{selected.name}</div>
                <div className="text-slate-500 dark:text-slate-500 text-sm mt-1 flex items-center gap-2"><Building2 size={12} /> {selected.company}</div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-slate-700 dark:text-slate-300"><Mail size={14} className="text-[#0891B2]" /> {selected.email}</div>
                {selected.phone && <div className="flex items-center gap-3 text-slate-700 dark:text-slate-300"><Phone size={14} className="text-[#0891B2]" /> {selected.phone}</div>}
                <div className="flex items-center gap-3 text-slate-700 dark:text-slate-300"><Calendar size={14} className="text-[#0891B2]" /> {new Date(selected.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2">Service</div>
                <div className="text-slate-900 dark:text-white">{selected.service}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2">Message</div>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-900">{selected.message}</p>
              </div>
              {selected.attachment && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2">Attachment</div>
                  <button
                    data-testid="download-attachment"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/files/${selected.attachment.id}`, { credentials: "include" });
                        if (!res.ok) throw new Error("Download failed");
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = selected.attachment.filename;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        toast.error("Failed to download");
                      }
                    }}
                    className="w-full flex items-center justify-between border border-[#0891B2]/40 bg-[#0891B2]/5 hover:bg-[#0891B2]/10 px-4 py-3 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Paperclip size={14} className="text-[#0891B2]" />
                      <div className="text-left">
                        <div className="text-sm text-slate-900 dark:text-white truncate max-w-[220px]">{selected.attachment.filename}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-500">{(selected.attachment.size / 1024).toFixed(1)} KB</div>
                      </div>
                    </div>
                    <Download size={14} className="text-slate-500 dark:text-slate-500 group-hover:text-slate-900" />
                  </button>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Update Status</div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`set-status-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`text-xs uppercase tracking-widest border px-3 py-2 transition-colors ${
                        selected.status === s
                          ? "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/5"
                          : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-900"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
